import React, { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Row, Col, CardBody, Spinner, Alert, Button } from "reactstrap"
import { getPurchasableAddons, getMyAddons } from "../../api/requests/addon-requests"
import { getPlanIntegrationCatalog } from "../../api/requests/subscription-requests"

const NAVY = "#221C5D"
const GREEN = "#71C30B"
const GRAY = "#B0B7C3"
const RED_LT = "#E07A7A"

const TableCard = ({ className = "", children, ...rest }) => (
  <div
    className={`card rounded-4 border-0 shadow-lg ${className}`}
    style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }}
    {...rest}
  >
    {children}
  </div>
)

const formatPrice = (v) => {
  if (v == null) return "-"
  return new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) + " ₺"
}
const formatDate = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "Süresiz")

const statusColor = (status, effective) => {
  if (status === "ACTIVE") return effective ? GREEN : GRAY
  if (status === "PENDING") return "#F4A259"
  if (status === "CANCELLED") return RED_LT
  return GRAY
}

/**
 * Ek modül pazaryeri — firma tarafı (V98).
 *
 * <h3>Neden "paketinize ek olarak" vurgusu var</h3>
 * Modüller paketin yerine geçmez, üstüne eklenir ve asla hak geri almaz.
 * Müşterinin bunu bir paket yükseltmesiyle karıştırması, yanlış ürün satın
 * almasına yol açardı.
 */
const AddonMarketplace = () => {
  const navigate = useNavigate()
  const [modules, setModules] = useState([])
  const [mine, setMine] = useState([])
  const [integrationLabels, setIntegrationLabels] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  document.title = "360 Market | Tahsilat360"

  const styles = `
    .addon-market .module-card {
      border: 1px solid #e9edf2; border-radius: 1rem; background: #fff;
      height: 100%; display: flex; flex-direction: column;
      transition: border-color .15s, box-shadow .15s;
    }
    .addon-market .module-card:hover { border-color: ${NAVY}; box-shadow: 0 .5rem 1.25rem rgba(10,49,110,.08); }
    .addon-market .grant-chip {
      display: inline-block; background: #EBF0F7; color: ${NAVY};
      border-radius: 999px; padding: .15rem .6rem; font-size: .74rem;
      font-weight: 600; margin: .1rem .2rem .1rem 0;
    }
    .addon-market .btn-navy { background: ${NAVY}; border-color: ${NAVY}; color: #fff; }
    .addon-market .btn-navy:hover { filter: brightness(1.05); color: #fff; }
  `

  const load = async () => {
    try {
      setError(null)
      setLoading(true)
      const [modulesRes, mineRes, catalogRes] = await Promise.all([
        getPurchasableAddons(),
        getMyAddons(),
        getPlanIntegrationCatalog().catch(() => null),
      ])
      setModules(modulesRes.data || [])
      setMine(mineRes.data || [])
      setIntegrationLabels(
        Object.fromEntries((catalogRes?.data || []).map((i) => [i.code, i.label]))
      )
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const grants = (m) => {
    const parts = []
    if (m.grantsMaxAccounts) parts.push(`+${m.grantsMaxAccounts} cari / bayi`)
    if (m.grantsMaxUsers) parts.push(`+${m.grantsMaxUsers} kullanıcı`)
    if (m.grantsCollectionAmount) parts.push(`+${formatPrice(m.grantsCollectionAmount)} tahsilat`)
    if (m.grantsCollectionCount) parts.push(`+${m.grantsCollectionCount} tahsilat adedi`)
    return parts
  }

  if (loading) {
    return (
      <div className="page-content">
        <div className="container-fluid text-center p-5">
          <Spinner size="lg" color="primary" />
          <div className="mt-3 text-muted">360 Market yükleniyor...</div>
        </div>
      </div>
    )
  }

  const activeMine = mine.filter((a) => a.status === "ACTIVE" || a.status === "PENDING")
  const ownership = (moduleId) => {
    const owned = mine.filter((a) => a.moduleId === moduleId)
    if (owned.some((a) => a.status === "ACTIVE" && a.effective)) return "ACTIVE"
    if (owned.some((a) => a.status === "PENDING")) return "PENDING"
    return null
  }
  const pastMine = mine.filter((a) => a.status !== "ACTIVE" && a.status !== "PENDING")

  return (
    <div className="page-content addon-market">
      <style>{styles}</style>
      <div className="container-fluid">
        {error && (
          <Alert color="danger" className="d-flex align-items-center justify-content-between">
            <span>{error}</span>
            <Button color="light" size="sm" onClick={load}>Tekrar Dene</Button>
          </Alert>
        )}

        <div className="mb-3">
          <h5 className="mb-1 fw-semibold" style={{ color: NAVY }}>360 Market</h5>
          <div className="text-muted" style={{ fontSize: ".85rem" }}>
            Mevcut paketinizi değiştirmeden, <strong>paketinize ek olarak</strong> hak satın alın.
          </div>
        </div>

        {/* Sahip olunan modüller */}
        {activeMine.length > 0 && (
          <TableCard className="mb-3">
            <CardBody className="p-4">
              <h6 className="fw-semibold mb-3">Modülleriniz</h6>
              <Row>
                {activeMine.map((a) => (
                  <Col md={6} xl={4} key={a.id} className="mb-3">
                    <div className="p-3 rounded-3" style={{ background: "#F7F9FC" }}>
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="fw-semibold">{a.moduleName}</div>
                        <span
                          className="badge border-0"
                          style={{
                            borderRadius: 999, fontWeight: 600, color: "#fff",
                            background: statusColor(a.status, a.effective),
                          }}
                        >
                          {a.statusLabel || a.status}
                        </span>
                      </div>
                      <div className="text-muted mt-1" style={{ fontSize: ".75rem" }}>
                        {a.endDate ? `${formatDate(a.endDate)} tarihine kadar` : "Süresiz"}
                      </div>
                      {a.status === "PENDING" && (
                        <div className="text-muted mt-1" style={{ fontSize: ".72rem" }}>
                          Ödeme onayı bekleniyor.
                        </div>
                      )}
                    </div>
                  </Col>
                ))}
              </Row>
            </CardBody>
          </TableCard>
        )}

        {/* Satın alınabilir modüller */}
        <TableCard>
          <CardBody className="p-4">
            <h6 className="fw-semibold mb-3">Satın Alınabilir Modüller</h6>

            {modules.length === 0 ? (
              <div className="text-center py-5">
                <i className="bx bx-extension text-muted" style={{ fontSize: 56 }} />
                <h6 className="mt-3 mb-1">Şu anda satışta modül yok</h6>
                <div className="text-muted" style={{ fontSize: ".85rem" }}>
                  Yeni modüller eklendiğinde burada listelenir.
                </div>
              </div>
            ) : (
              <Row>
                {modules.map((m) => (
                  <Col md={6} xl={4} key={m.id} className="mb-3">
                    <div className="module-card p-4">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="fw-semibold mb-0">{m.name}</h6>
                        <span
                          className="badge border-0"
                          style={{ borderRadius: 999, background: "#EBF0F7", color: NAVY, fontWeight: 600 }}
                        >
                          {m.lifecycleLabel}
                        </span>
                      </div>

                      {m.description && (
                        <div className="text-muted mb-3" style={{ fontSize: ".8rem" }}>{m.description}</div>
                      )}

                      <div className="mb-3">
                        {grants(m).map((g) => <span key={g} className="grant-chip">{g}</span>)}
                        {(m.moduleFeatures || []).length > 0 && (
                          <span className="grant-chip">{m.moduleFeatures.length} ek özellik</span>
                        )}
                        {(m.moduleIntegrations || []).map((code) => (
                          <span key={code} className="grant-chip">
                            <i className="bx bx-plug me-1" />
                            {integrationLabels[code] || code}
                          </span>
                        ))}
                      </div>

                      {m.lifecycle === "CONSUMABLE" && m.validityDays && (
                        <div className="text-muted mb-2" style={{ fontSize: ".74rem" }}>
                          <i className="bx bx-time-five me-1" />
                          {m.validityDays} gün geçerli
                        </div>
                      )}
                      {m.lifecycle === "SUBSCRIPTION" && (
                        <div className="text-muted mb-2" style={{ fontSize: ".74rem" }}>
                          <i className="bx bx-calendar me-1" />
                          Mevcut abonelik döneminizin sonuna kadar geçerli
                        </div>
                      )}

                      <div className="mt-auto pt-3 border-top">
                        <div className="d-flex align-items-baseline justify-content-between mb-3">
                          <div>
                            <div className="h5 mb-0" style={{ color: NAVY }}>{formatPrice(m.totalPrice)}</div>
                            <div className="text-muted" style={{ fontSize: ".72rem" }}>KDV dâhil</div>
                          </div>
                        </div>
                        {ownership(m.id) === "ACTIVE" ? (
                          <Button color="light" className="w-100" style={{ borderRadius: 999 }} disabled>
                            <i className="bx bx-check me-1" style={{ color: GREEN }} />
                            Firmanızda tanımlı
                          </Button>
                        ) : ownership(m.id) === "PENDING" ? (
                          <Button color="light" className="w-100" style={{ borderRadius: 999 }} disabled>
                            Ödeme onayı bekleniyor
                          </Button>
                        ) : (
                          <Button
                            className="btn-navy w-100"
                            style={{ borderRadius: 999 }}
                            onClick={() => navigate(`/subscription/addons/checkout/${m.id}`)}
                          >
                            Satın Al
                          </Button>
                        )}
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            )}
          </CardBody>
        </TableCard>

        {/* Geçmiş */}
        {pastMine.length > 0 && (
          <TableCard className="mt-3">
            <CardBody className="p-4">
              <h6 className="fw-semibold mb-3">Geçmiş Modüller</h6>
              {pastMine.map((a) => (
                <div key={a.id} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                  <div>
                    <div style={{ fontSize: ".88rem" }}>{a.moduleName}</div>
                    <div className="text-muted" style={{ fontSize: ".72rem" }}>
                      {formatDate(a.startDate)} → {a.endDate ? formatDate(a.endDate) : "—"}
                    </div>
                  </div>
                  <span className="text-muted" style={{ fontSize: ".8rem" }}>{a.statusLabel || a.status}</span>
                </div>
              ))}
            </CardBody>
          </TableCard>
        )}
      </div>
    </div>
  )
}

export default AddonMarketplace
